/** @jsxRuntime automatic */
/** @jsxImportSource react */
import React, { useContext, useEffect, useId, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { TbDatabase, TbFilter, TbX } from "react-icons/tb"
import { dashboardKpiRequestKey, resolveDashboardKpi } from "@/datox/dashboard-kpi"
import { DashboardKpiContext, kpiIssueMessage } from "./KpiWidget"
import "./kpi-source-panel.css"

export function KpiSourcePanel(props) {
  const kpi = useContext(DashboardKpiContext)
  const labels = props.labels.kpi
  const titleID = useId()
  const dialog = useRef(null)
  const close = useRef(props.onClose)
  close.current = props.onClose
  const [response, setResponse] = useState(null)
  const [allColumns, setAllColumns] = useState(false)
  const [retry, setRetry] = useState(0)
  const key = `${dashboardKpiRequestKey(props.widget, props.filterWidgets || [], kpi.sourceKeys?.[props.widget.data?.dataset_id] ?? kpi.sourceKey)}:${retry}`
  const result = response?.key === key ? response.value : null
  const sourceID = result?.source?.dataset_id || props.widget.data?.dataset_id
  const sourceName = result?.source?.name || kpi.datasets?.[sourceID]?.table?.name || labels.sourceSelectedDataset
  const rows = result?.evidence || []
  const referenced = result?.referenced_columns || []
  const headers = [...new Set(rows.flatMap((row) => Object.keys(row)))]
  const columns = !allColumns && referenced.length ? referenced : headers
  const filters = result?.constraints?.filters || []
  const dashboardFilters = result?.constraints?.dashboard_filters || []
  const method = props.widget.data?.aggregation || "sum"
  const aggregationLabel =
    {
      sum: props.labels.aggregationSum,
      avg: props.labels.aggregationAverage,
      min: props.labels.aggregationMin,
      max: props.labels.aggregationMax,
      count: props.labels.aggregationCount,
      first: props.labels.aggregationFirst,
      last: props.labels.aggregationLast,
    }[method] || method

  useEffect(() => {
    let active = true
    setAllColumns(false)
    if (!kpi.onEvaluateKpi) {
      setResponse({ key, value: { status: "error" } })
      return undefined
    }
    kpi
      .onEvaluateKpi(props.widget, props.filterWidgets || [], 20)
      .then((value) => {
        if (active) setResponse({ key, value })
      })
      .catch(() => {
        if (active) setResponse({ key, value: { status: "error" } })
      })
    return () => {
      active = false
    }
  }, [key])

  useEffect(() => {
    const previous = document.activeElement
    dialog.current?.querySelector("button")?.focus()
    return () => {
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus()
    }
  }, [])

  // Portal out of the scaled dashboard canvas so sizing and scrolling follow the viewport.
  return createPortal(
    <div
      className="datox-kpi-source-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) close.current()
      }}
    >
      <section
        className="datox-kpi-source"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleID}
        ref={dialog}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault()
            event.stopPropagation()
            close.current()
            return
          }
          if (event.key !== "Tab") return
          const items = [...dialog.current.querySelectorAll('button:not(:disabled), summary, [tabindex="0"]')].filter(
            (item) => item.getClientRects().length,
          )
          const first = items[0]
          const last = items[items.length - 1]
          if (!first) {
            event.preventDefault()
            dialog.current.focus()
            return
          }
          if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
            event.preventDefault()
            last.focus()
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
          }
        }}
      >
        <header className="datox-kpi-source__header">
          <div className="datox-kpi-source__heading">
            <span className="datox-kpi-source__icon">
              <TbDatabase size={22} aria-hidden="true" />
            </span>
            <div>
              <h2 id={titleID}>{labels.explain}</h2>
              <p>{props.widget.title || labels.sourceSubtitle}</p>
            </div>
          </div>
          <button
            type="button"
            className="datox-kpi-source__close"
            aria-label={labels.sourceClose}
            onClick={props.onClose}
          >
            <TbX size={20} aria-hidden="true" />
          </button>
        </header>

        <div className="datox-kpi-source__body" aria-busy={!result}>
          <div className="datox-kpi-source__summary">
            <div className="datox-kpi-source__result" role="status">
              <span className="datox-kpi-source__label">{labels.sourceResult}</span>
              <strong>
                {!result
                  ? labels.loading
                  : result.status === "ready"
                    ? resolveDashboardKpi(result.value, props.widget.style || {}, kpi.locale).displayValue ||
                      labels.invalid
                    : result.status === "unavailable"
                      ? labels.unavailable
                      : labels.error}
              </strong>
              {result && ["ready", "unavailable"].includes(result.status) ? (
                <span>{labels.sourceRows.replace("{count}", String(result.row_count))}</span>
              ) : null}
            </div>
            <div className="datox-kpi-source__calculation">
              <span className="datox-kpi-source__label">
                {props.widget.kpi_calculation ? labels.formula : labels.aggregation}
              </span>
              {props.widget.kpi_calculation ? (
                <code>{props.widget.kpi_calculation.expression}</code>
              ) : (
                <div className="datox-kpi-source__aggregation">
                  <strong>{aggregationLabel}</strong>
                  {method !== "count" && props.widget.data?.metric ? <code>{props.widget.data.metric}</code> : null}
                </div>
              )}
            </div>
          </div>

          {result?.status === "invalid" ? (
            <p className="datox-kpi-source__error" role="alert">
              {kpiIssueMessage(labels, result.error)}
            </p>
          ) : null}
          {result?.status === "error" ? (
            <div className="datox-kpi-source__error" role="alert">
              <span>{labels.error}</span>
              <button type="button" onClick={() => setRetry((value) => value + 1)}>
                {labels.sourceRetry}
              </button>
            </div>
          ) : null}

          <section className="datox-kpi-source__section">
            <h3>
              <TbDatabase size={17} aria-hidden="true" />
              {labels.sourceData}
            </h3>
            <p className="datox-kpi-source__dataset">{sourceName}</p>
            {referenced.length ? (
              <div className="datox-kpi-source__columns" aria-label={labels.sourceFields}>
                {referenced.map((column) => (
                  <code key={column}>{column}</code>
                ))}
              </div>
            ) : null}
            {sourceID ? (
              <details className="datox-kpi-source__technical">
                <summary>{labels.sourceTechnical}</summary>
                <code>{sourceID}</code>
              </details>
            ) : null}
          </section>

          {result && ["ready", "unavailable"].includes(result.status) ? (
            <>
              <section className="datox-kpi-source__section">
                <h3>
                  <TbFilter size={17} aria-hidden="true" />
                  {labels.sourceConstraints}
                </h3>
                {!filters.length &&
                !dashboardFilters.length &&
                !result.constraints?.sort?.column &&
                !result.constraints?.row_limit ? (
                  <p className="datox-kpi-source__muted">{labels.sourceNoConstraints}</p>
                ) : (
                  <ul className="datox-kpi-source__rules">
                    {dashboardFilters.map((filter, index) => (
                      <li key={`dashboard-${index}`}>
                        <code>{filter.column}</code>
                        <span>{props.labels.filterEquals || "="}</span>
                        <strong>{filter.selected_values.join(", ")}</strong>
                      </li>
                    ))}
                    {filters.map((filter, index) => (
                      <li key={`widget-${index}`}>
                        <code>{filter.column}</code>
                        <span>
                          {filter.operator === "notEmpty"
                            ? props.labels.filterNotEmpty
                            : filter.operator === "equals"
                              ? props.labels.filterEquals
                              : props.labels.filterContains}
                        </span>
                        {filter.operator !== "notEmpty" ? <strong>{String(filter.value ?? "")}</strong> : null}
                      </li>
                    ))}
                    {result.constraints?.sort?.column ? (
                      <li>
                        <span>{labels.sourceSort.replace("{column}", result.constraints.sort.column)}</span>
                        <strong>
                          {result.constraints.sort.direction === "desc"
                            ? props.labels.sortDescending
                            : props.labels.sortAscending}
                        </strong>
                      </li>
                    ) : null}
                    {result.constraints?.row_limit ? (
                      <li>{labels.sourceLimit.replace("{count}", String(result.constraints.row_limit))}</li>
                    ) : null}
                  </ul>
                )}
              </section>

              <section className="datox-kpi-source__section">
                <div className="datox-kpi-source__evidence-heading">
                  <div>
                    <h3>{labels.sourceEvidence}</h3>
                    <p className="datox-kpi-source__muted">
                      {labels.sourceEvidenceHelp
                        .replace("{shown}", String(rows.length))
                        .replace("{total}", String(result.row_count))}
                    </p>
                  </div>
                  {referenced.length > 0 && headers.some((header) => !referenced.includes(header)) ? (
                    <button
                      type="button"
                      className="datox-kpi-source__toggle"
                      aria-pressed={allColumns}
                      onClick={() => setAllColumns((value) => !value)}
                    >
                      {allColumns ? labels.sourceRelevantColumns : labels.sourceAllColumns}
                    </button>
                  ) : null}
                </div>
                {rows.length ? (
                  <div
                    className="datox-kpi-source__table-scroll"
                    role="region"
                    aria-label={labels.sourceEvidence}
                    tabIndex={0}
                  >
                    <table>
                      <caption>{labels.sourceEvidence}</caption>
                      <thead>
                        <tr>
                          <th scope="col">#</th>
                          {columns.map((column) => (
                            <th scope="col" key={column}>
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr key={index}>
                            <th scope="row">{index + 1}</th>
                            {columns.map((column) => (
                              <td
                                key={column}
                                className={typeof row[column] === "number" ? "datox-kpi-source__numeric" : undefined}
                              >
                                {row[column] === null || row[column] === undefined || row[column] === "" ? (
                                  <span aria-label={labels.missing}>—</span>
                                ) : (
                                  String(row[column])
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="datox-kpi-source__empty">{labels.sourceNoRows}</p>
                )}
              </section>
            </>
          ) : null}
        </div>
        <footer className="datox-kpi-source__footer">
          <button type="button" onClick={props.onClose}>
            {labels.sourceClose}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}
